import { apiRequest } from "./apiClient";
import type { ManagedUserRow } from "../types/userManagement";

type PaginatedResponse<T> = {
  readonly data: T[];
  readonly meta?: {
    readonly total?: number;
    readonly limit?: number;
    readonly offset?: number;
  };
};

type BackendIdentityItem = {
  readonly user_id: string;
  readonly user_name: string;
  readonly email: string;
  readonly last_login_at: string | null;
  readonly business_units?: readonly string[];
  readonly admin_roles?: readonly string[];
  readonly groups?: readonly string[];
};

const toRow = (item: BackendIdentityItem): ManagedUserRow => ({
  userId: item.user_id,
  userName: item.user_name,
  email: item.email,
  lastLoginAt: item.last_login_at ?? null,
  businessUnits: Array.isArray(item.business_units) ? item.business_units : [],
  adminRoles: Array.isArray(item.admin_roles) ? item.admin_roles : [],
  groups: Array.isArray(item.groups) ? item.groups : []
});

export type ListManagedUsersParams = {
  readonly q?: string;
  readonly limit: number;
  readonly offset: number;
};

export type ListManagedUsersResult = {
  readonly items: ManagedUserRow[];
  readonly total: number;
};

export const listManagedUsers = async (
  params: ListManagedUsersParams
): Promise<ListManagedUsersResult> => {
  const sp = new URLSearchParams();
  sp.set("limit", String(params.limit));
  sp.set("offset", String(params.offset));
  if (params.q?.trim()) sp.set("q", params.q.trim());
  const res = await apiRequest(`/user-management/users?${sp.toString()}`);
  const json = (await res.json()) as PaginatedResponse<BackendIdentityItem>;
  const items = (json.data ?? []).map(toRow);
  const total = json.meta?.total ?? items.length;
  return { items, total };
};

type SuccessResponse<T> = { readonly success: boolean; readonly data: T };

export const updateManagedUserAdminRoles = async (
  userId: string,
  adminRoles: readonly string[]
): Promise<ManagedUserRow> => {
  const res = await apiRequest(`/user-management/users/${userId}/admin-roles`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ admin_roles: [...adminRoles] })
  });
  const json = (await res.json()) as SuccessResponse<BackendIdentityItem>;
  return toRow(json.data);
};
